import { get, set } from 'idb-keyval';
import type {
    TransitData,
    Agency,
    Route,
    Stop,
    Trip,
    LocationsResponse,
    AgencySearchResponse,
    AgencyLogoResponse,
    ArrivalsResponse,
    RoutesResponse,
    StopsResponse,
    TripsResponse
} from './types';

const DEFAULT_API_BASE = 'https://maindashboard.commuterhubdashboard.workers.dev/api';
export const API_BASE = (import.meta.env.VITE_API_BASE || DEFAULT_API_BASE).replace(/\/+$/, '');
const REGISTER_API_TOKEN = import.meta.env.VITE_REGISTER_API_TOKEN || '';

const fetchJsonWithTimeout = async <T>(url: string, timeoutMs = 12000, retries = 1): Promise<T> => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const res = await fetch(url, { signal: controller.signal });
            if (!res.ok) {
                const raw = await res.text().catch(() => '');
                let message = `Request failed with ${res.status}`;
                if (raw) {
                    try {
                        const parsed = JSON.parse(raw);
                        if (typeof parsed?.error === 'string') {
                            message = parsed.error;
                        } else if (typeof parsed?.message === 'string') {
                            message = parsed.message;
                        }
                    } catch {
                        message = raw.length > 180 ? `${raw.slice(0, 180)}...` : raw;
                    }
                }
                throw new Error(message);
            }
            return await res.json() as T;
        } catch (error) {
            lastError = error;
            const isAbort = error instanceof Error && error.name === 'AbortError';
            if (!isAbort) {
                console.error(`Fetch attempt ${attempt + 1}/${retries + 1} failed:`, error);
            }
            if (attempt === retries) throw error;
        } finally {
            clearTimeout(timer);
        }
    }
    throw lastError || new Error('Request failed');
};

export async function fetchCountries(): Promise<string[]> {
    const data = await fetchJsonWithTimeout<LocationsResponse>(`${API_BASE}/locations/countries`, 12000, 2);
    if (data?.error) throw new Error(data.error);
    return data.countries || [];
}

export async function fetchStates(country: string): Promise<string[]> {
    const url = new URL(`${API_BASE}/locations/states`);
    url.searchParams.set('country', country);
    const data = await fetchJsonWithTimeout<LocationsResponse>(url.toString(), 12000, 2);
    if (data?.error) throw new Error(data.error);
    return data.states || [];
}

export async function fetchCities(country: string, state: string): Promise<string[]> {
    const url = new URL(`${API_BASE}/locations/cities`);
    url.searchParams.set('country', country);
    url.searchParams.set('state', state);
    const data = await fetchJsonWithTimeout<LocationsResponse>(url.toString(), 12000, 2);
    if (data?.error) throw new Error(data.error);
    return data.cities || [];
}

export async function fetchAgenciesByLocation(country: string, state?: string, city?: string): Promise<Agency[]> {
    const url = new URL(`${API_BASE}/agency-search`);
    url.searchParams.set('country', country);
    if (state) url.searchParams.set('state', state);
    if (city) url.searchParams.set('city', city);
    const data = await fetchJsonWithTimeout<AgencySearchResponse>(url.toString(), 15000, 1);
    if (data?.error) throw new Error(data.error);
    return data.agencies || [];
}

export async function fetchAgencyLogo(name: string, wikidataId?: string | null): Promise<string | null> {
    const url = new URL(`${API_BASE}/agency-logo`);
    url.searchParams.set('name', name);
    if (wikidataId) url.searchParams.set('wikidata', wikidataId);
    const data = await fetchJsonWithTimeout<AgencyLogoResponse>(url.toString(), 12000, 1);
    if (data?.error) throw new Error(data.error);
    return data.logoUrl || null;
}

export async function fetchRoutes(agencyId: string): Promise<RoutesResponse> {
    const data = await fetchJsonWithTimeout<RoutesResponse>(`${API_BASE}/routes/${agencyId}`, 12000, 1);
    if (data?.error) {
        throw new Error(data.error);
    }
    return { routes: data.routes || [] };
}

export async function fetchStops(routeId: string, direction?: string | string[]): Promise<StopsResponse> {
    const url = new URL(`${API_BASE}/stops/${routeId}`);
    if (Array.isArray(direction)) {
        direction.forEach((dir) => {
            if (dir) url.searchParams.append('direction', dir);
        });
    } else if (direction) {
        url.searchParams.set('direction', direction);
    }
    const data = await fetchJsonWithTimeout<StopsResponse>(url.toString(), 12000, 1);
    if (data?.error) {
        throw new Error(data.error);
    }
    return { stops: data.stops || [] };
}

export async function fetchTrips(routeId: string, includeAll = false): Promise<TripsResponse> {
    const url = new URL(`${API_BASE}/trips/${routeId}`);
    if (includeAll) url.searchParams.set('include_all', '1');
    const data = await fetchJsonWithTimeout<TripsResponse>(url.toString(), 12000, 1);
    if (data?.error) {
        throw new Error(data.error);
    }
    return { trips: data.trips || [], sampleHeadsigns: data.sampleHeadsigns };
}

export async function fetchArrivals(stopId: string, direction?: string, windowMinutes?: number, signal?: AbortSignal): Promise<ArrivalsResponse> {
    try {
        const url = new URL(`${API_BASE}/arrivals/${stopId}`);
        if (direction) url.searchParams.set('direction', direction);
        if (windowMinutes) url.searchParams.set('window', String(windowMinutes));
        const response = await fetch(url.toString(), { signal });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage =
                (typeof errorData?.error === 'string' && errorData.error) ||
                errorData?.error?.message ||
                'API unavailable';
            throw new Error(errorMessage);
        }

        const result: ArrivalsResponse = await response.json();
        const data = result.data;

        // Handle structured error responses
        if (result.error) {
            console.warn('API returned error:', result.error);
        }

        // Cache for offline mode
        await set(`cache_arrivals_${stopId}${direction ? `_${direction}` : ''}${windowMinutes ? `_${windowMinutes}` : ''}`, data);
        return result;
    } catch (error) {
        // Don't cache fallback if request was aborted
        if (error instanceof Error && error.name === 'AbortError') {
            console.log('Fetch aborted (new request started)');
            throw error;
        }
        console.error('Fetch failed, trying cache...', error);
        const cached = await get(`cache_arrivals_${stopId}${direction ? `_${direction}` : ''}${windowMinutes ? `_${windowMinutes}` : ''}`);
        if (cached) return { data: cached as TransitData, source: 'cache' };
        throw error;
    }
}

export function subscribeToLive(stopId: string, onUpdate: (data: TransitData) => void, direction?: string, onError?: (error: Error) => void) {
    let eventSource: EventSource | null = null;
    let reconnectAttempts = 0;
    let reconnectTimer: number | null = null;
    let isClosed = false;
    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY = 2000; // 2 seconds

    const connect = () => {
        if (isClosed) return;

        const url = new URL(`${API_BASE}/live/${stopId}`);
        if (direction) url.searchParams.set('direction', direction);
        eventSource = new EventSource(url.toString());

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                onUpdate(data);
                // Reset reconnect attempts on successful message
                reconnectAttempts = 0;
            } catch (err) {
                console.error('SSE parse error:', err);
            }
        };

        eventSource.onerror = (err) => {
            console.error('SSE Error:', err);
            eventSource?.close();

            // FIXED: Implement exponential backoff reconnection
            if (!isClosed && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                const delay = Math.min(BASE_DELAY * Math.pow(2, reconnectAttempts), 30000); // Max 30s
                reconnectAttempts++;
                console.log(`SSE reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);

                reconnectTimer = setTimeout(() => {
                    connect();
                }, delay) as unknown as number;
            } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
                console.error('SSE max reconnection attempts reached, giving up');
                if (onError) {
                    onError(new Error('SSE connection failed after multiple attempts'));
                }
            }
        };

        eventSource.onopen = () => {
            console.log('SSE connection established');
            reconnectAttempts = 0;
        };
    };

    // Initial connection
    connect();

    // Return cleanup function
    return () => {
        isClosed = true;
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
        }
        eventSource?.close();
    };
}

export async function registerDevice(fingerprint: string) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (REGISTER_API_TOKEN) {
        headers['X-Register-Token'] = REGISTER_API_TOKEN;
    }

    const response = await fetch(`${API_BASE}/register`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ fingerprint, name: 'Kiosk-1' })
    });
    if (!response.ok) {
        const raw = await response.text().catch(() => '');
        throw new Error(raw || `Device registration failed with ${response.status}`);
    }
    return response.json();
}

export async function fetchWeather(latitude: number, longitude: number) {
    try {
        // Use Open-Meteo API (free, no API key required)
        const url = new URL('https://api.open-meteo.com/v1/forecast');
        url.searchParams.set('latitude', latitude.toString());
        url.searchParams.set('longitude', longitude.toString());
        url.searchParams.set('current', 'temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m');
        url.searchParams.set('temperature_unit', 'celsius'); // Always fetch in Celsius, convert if needed
        url.searchParams.set('wind_speed_unit', 'kmh');
        url.searchParams.set('timezone', 'auto');

        const response = await fetchJsonWithTimeout(url.toString(), 8000, 1);
        return response;
    } catch (error) {
        console.error('Weather fetch failed:', error);
        throw error;
    }
}
